'use client'

import { useState, useRef, useEffect } from 'react'

export default function HeroVideo() {
    const [isPlaying, setIsPlaying] = useState(false)
    const videoRef = useRef<HTMLVideoElement>(null)

    useEffect(() => {
        // Set video to 3 seconds to show a nice frame as preview (originally 4 seconds, but we trimmed 1 second)
        // Wrapped in try/catch to prevent iOS Safari crashes (InvalidStateError if video metadata hasn't loaded)
        if (videoRef.current && !isPlaying) {
            try {
                if (videoRef.current.readyState > 0) {
                    videoRef.current.currentTime = 3;
                } else {
                    const setTime = () => {
                        if (videoRef.current) videoRef.current.currentTime = 3;
                    };
                    videoRef.current.addEventListener('loadedmetadata', setTime);
                    return () => videoRef.current?.removeEventListener('loadedmetadata', setTime);
                }
            } catch (e) {
                console.warn('Could not set video start time', e);
            }
        }
    }, [isPlaying])

    return (
        <div className="relative max-w-4xl mx-auto mb-12">
            {/* Container with 16:9 aspect ratio to match video */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/50 border border-gray-700/50 bg-gray-900" style={{ aspectRatio: '16 / 9' }}>
                <video
                    ref={videoRef}
                    controls={isPlaying}
                    loop
                    playsInline
                    preload="metadata"
                    poster="/images/linkbox_holotube_m5.png"
                    className="w-full h-full object-cover"
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                >
                    <source src="/linkbox-demo.mp4#t=3" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>

                {/* Simple play button overlay when not playing */}
                {!isPlaying && (
                    <div className="absolute inset-0 flex items-center justify-center cursor-pointer"
                        onClick={(e) => {
                            const video = videoRef.current
                            if (video) {
                                video.currentTime = 0
                                video.play().catch((error) => {
                                    console.warn('Could not play hero video', error)
                                })
                            }
                        }}>
                        {/* Just the play button centered */}
                        <div className="w-24 h-24 rounded-full bg-[#4ade80]/95 backdrop-blur-sm flex items-center justify-center shadow-2xl shadow-green-500/50 hover:bg-[#22c55e] hover:scale-110 transition-all">
                            {/* Triangle play icon */}
                            <svg className="w-12 h-12 text-gray-900 ml-1" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                            </svg>
                        </div>
                    </div>
                )}

                {/* Gradient overlay at bottom */}
                <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-gray-950 to-transparent pointer-events-none" />
            </div>
            {/* Glow effect behind video */}
            <div className="absolute -inset-4 bg-gradient-to-r from-[#4ade80]/20 to-[#22c55e]/20 rounded-3xl blur-2xl -z-10" />
        </div>
    )
}
